import { Alert, Stack, Typography, useTheme, IconButton, Divider, Sheet } from '@mui/joy';
import { useState, type ReactNode } from 'react';
import { useAuth } from 'components/management/NewAuthProvider';
import { getErrorLayout, type ErrorVariant } from 'components/technical/errors/errorLayouts';
import { useFinalColorScheme } from 'technical/useFinalColorScheme';
import { calculateErrorMessage, DEFAULT_MESSAGE } from './GraphQLAPIService';

const safeStringify = (value: unknown): string => {
  try {
    if (value instanceof Error) {
      return `Error: ${value.name}\nMessage: ${value.message}\nStack:\n${value.stack}`;
    }
    return JSON.stringify(value, null, 2);
  } catch (error) {
    console.error('Failed to stringify error', error);
    return 'Failed to stringify error';
  }
};

export interface ErrorViewProps {
  cause?: unknown;
  className?: string;
  variant?: ErrorVariant;
  children?: ReactNode;
}

export interface CustomizableErrorViewProps extends ErrorViewProps {
  content?: ReactNode;
  title?: string;
}

const DEFAULT_TITLE = 'Oops! Something went wrong';

export const CustomizableErrorView = ({
  cause,
  content,
  title = DEFAULT_TITLE,
  className,
  variant = 'inline',
  children,
}: CustomizableErrorViewProps) => {
  const mode = useFinalColorScheme();
  const isDarkMode = mode === 'dark';
  const theme = useTheme();
  const { isAdmin } = useAuth();
  const [showRaw, setShowRaw] = useState(false);
  const layout = getErrorLayout(variant);

  const titleLevel = layout.headerLevel;

  const finalContent = content ?? DEFAULT_MESSAGE;

  return (
    <Alert color="neutral" variant="soft" className={className} sx={layout.sx}>
      <Stack direction="column" gap={1} width="100%" p={2}>
        <Typography level={titleLevel} fontWeight="lg">
          😢 {title}
        </Typography>

        <Typography level="body-xs" mt={-0.5}>
          {finalContent}
        </Typography>

        {isAdmin && !!cause && (
          <>
            <Divider />
            <Sheet variant="outlined" color="neutral" sx={{ mt: 1, px: 1, borderRadius: 'sm' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography level="body-xs" fontWeight="lg">
                  Technical details
                </Typography>
                <IconButton
                  size="sm"
                  sx={{ minHeight: theme.spacing(4), fontSize: theme.vars.fontSize.xs }}
                  variant="plain"
                  color="neutral"
                  onClick={() => setShowRaw((prev) => !prev)}
                >
                  {showRaw ? 'Hide' : 'Show'}
                </IconButton>
              </Stack>

              {showRaw && (
                <pre
                  style={{
                    marginTop: theme.spacing(1),
                    fontSize: theme.vars.fontSize.xs,
                    padding: theme.spacing(1),
                    borderRadius: theme.vars.radius.sm,
                    overflowX: 'auto',
                    maxHeight: '400px',
                    background: isDarkMode ? theme.vars.palette.neutral[800] : theme.vars.palette.danger.softBg,
                    color: isDarkMode ? theme.vars.palette.common.white : theme.vars.palette.danger[800],
                    border: `1px solid ${
                      isDarkMode ? theme.vars.palette.neutral[700] : theme.vars.palette.danger.outlinedBorder
                    }`,
                    // keep some padding on the sides to not overflow the screen when the info is displayed in a modal
                    maxWidth: 'calc(100vw - 200px)',
                  }}
                >
                  {safeStringify(cause)}
                </pre>
              )}
            </Sheet>
          </>
        )}

        {children}
      </Stack>
    </Alert>
  );
};

export const ErrorView = ({ cause, className, variant = 'inline', children }: ErrorViewProps) => {
  const message = calculateErrorMessage(cause);

  return (
    <CustomizableErrorView cause={cause} content={message} className={className} variant={variant}>
      {children}
    </CustomizableErrorView>
  );
};
